import { RefObject, useCallback, useMemo, useState } from 'react';

import { Nullable } from '@/utils/globalTypes';

import useEvent from './useEvent';

/**
 * We have to check if the scroll amount is close enough to some threshold in order to
 * more accurately calculate arrivedState. This is because scrollTop/scrollLeft are non-rounded
 * numbers, while scrollHeight/scrollWidth and clientHeight/clientWidth are rounded.
 * https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#determine_if_an_element_has_been_totally_scrolled
 */
const ARRIVED_STATE_THRESHOLD_PIXELS = 1;

export type UseScrollParams = {
  element: RefObject<Nullable<HTMLElement | Window | Document>>;
  /**
   * Offset arrived states by pixels
   *
   */
  offset?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  /**
   * Throttle time for scroll event in milliseconds - it’s disabled by default.
   *
   * @default undefined
   */
  throttle?: number | undefined;
};

const useScroll = ({ element, offset, throttle }: UseScrollParams) => {
  const [internalX, setInternalX] = useState(0);
  const [internalY, setInternalY] = useState(0);
  const [internalTopArrivedState, setInternalTopArrivedState] = useState(true);
  const [internalBottomArrivedState, setInternalBottomArrivedState] =
    useState(false);
  const [internalLeftArrivedState, setInternalLeftArrivedState] =
    useState(true);
  const [internalRightArrivedState, setInternalRightArrivedState] =
    useState(false);

  const setState = useCallback(
    (target: Nullable<HTMLElement | SVGElement | Window | Document>) => {
      const el: Element =
        (target as Window)?.document?.documentElement ||
        (target as Document)?.documentElement ||
        (target as HTMLElement);

      const {
        scrollLeft,
        scrollTop,
        scrollWidth,
        scrollHeight,
        clientHeight,
        clientWidth,
      } = el;

      // Rounds scroll values
      const _scrollTop = Math.abs(scrollTop);
      const _scrollBottom = Math.abs(scrollTop) + clientHeight;
      const _scrollLeft = Math.abs(scrollLeft);
      const _scrollRight = Math.abs(scrollLeft) + clientWidth;

      /*
       * Compensates scroll according to offsets and checks if the scroll
       * has reached the top, bottom, left or right of the element
       */
      const arrived = {
        top: _scrollTop <= (offset?.top ?? 0),
        bottom:
          _scrollBottom >=
          scrollHeight - (offset?.bottom ?? 0) - ARRIVED_STATE_THRESHOLD_PIXELS,
        left: _scrollLeft <= (offset?.left ?? 0),
        right:
          _scrollRight >=
          scrollWidth - (offset?.right ?? 0) - ARRIVED_STATE_THRESHOLD_PIXELS,
      };

      setInternalX(scrollLeft);
      setInternalY(scrollTop);
      setInternalTopArrivedState(arrived.top);
      setInternalBottomArrivedState(arrived.bottom);
      setInternalLeftArrivedState(arrived.left);
      setInternalRightArrivedState(arrived.right);
    },
    [offset?.bottom, offset?.left, offset?.right, offset?.top],
  );

  const measure = useCallback(() => {
    if (!element.current) return;

    setState(element.current);
  }, [setState, element]);

  const arrivedState = useMemo(
    () => ({
      top: internalTopArrivedState,
      bottom: internalBottomArrivedState,
      left: internalLeftArrivedState,
      right: internalRightArrivedState,
    }),
    [
      internalTopArrivedState,
      internalBottomArrivedState,
      internalLeftArrivedState,
      internalRightArrivedState,
    ],
  );

  useEvent({
    name: 'scroll',
    handler: () => {
      if (!element.current) return;

      setState(element.current);
    },
    target: element.current,
    throttle,
    options: {
      passive: true,
    },
  });

  return {
    x: internalX,
    y: internalY,
    arrivedState,
    measure,
  };
};

export default useScroll;
